import {difference, every, forEach, isEmpty, isNil, map, pick} from 'lodash';
import {createSelector} from 'reselect';

import {screenPropertiesDefaultState} from './auto_layout_reducer';
import AutoLayoutService from 'src/js/lib/auto_layout/auto_layout_service';
import {MetadataSchema} from 'src/js/model/auto_layout_metadata_schema';
import {
  ScreenBannerConfig,
  SCREEN_BANNER_CONFIG,
} from 'src/js/model/auto_layout_screen_banner_config';
import {
  KEYWORD_DATA_CONTENT,
  KEYWORD_DATA_ITEMS,
  KEYWORD_SCREEN_ID,
} from 'src/js/model/auto_layout_screen_data_helpers';
import {MINI_SCREENS_VIEWS_DOCUMENT} from 'src/js/model/mini_screens_config';

const STATIC_EMPTY_METADATA_SCHEMA = new MetadataSchema();

const getArrayContentSelector = (state, props) =>
  props.metadata.getContent().getComponent(props.content.getRef());

const getArrayLayoutSelector = (state, props) =>
  props.metadata.getLayout().getComponent(props.layout.getExpandLayout());

const getPropsScreenId = (state, props) => props.screenId;
const getPropsPathToData = (state, props) => props.pathToData;
const getStateDataChanges = (state, props) =>
  state[props.autoLayoutStateId].dataChanges;
const getStateDataCache = (state, props) =>
  state[props.autoLayoutStateId].dataCache;
const getStateLoadedDataCache = (state, props) =>
  state[props.autoLayoutStateId].loadedDataCache;
const getStateMetadataCache = (state, props) =>
  state[props.autoLayoutStateId].metadataCache;
const getMiniScreensConfig = (state, props) =>
  state[props.autoLayoutStateId].miniScreensConfig;
const getScreenProperties = (state, props) =>
  state[props.autoLayoutStateId].screenProperties;
const getSelectedScreenIds = (state, props) =>
  state[props.autoLayoutStateId].selectedScreenIds;
const getStateIndexCheckings = (state, props) =>
  state[props.autoLayoutStateId].indexCheckings;
const getStateConflictLastChangeAuthor = (state, props) =>
  state[props.autoLayoutStateId].conflictLastChangeAuthor;
const getDataSets = (state, props) => state[props.configStateId].dataSets;

// optionally get currentContextParams from props, to allow passing contextParams to selector
const getCurrentContextParams = (state, props) =>
  props.currentContextParams ||
  state[props.autoLayoutStateId].currentContextParams;

export const getArrayWrapperIndex = createSelector(
  [getStateDataCache, getPropsScreenId, getCurrentContextParams],
  (dataCache, screenId, contextParams) =>
    dataCache[AutoLayoutService.getIndexScreenId(screenId, contextParams)]
);

export const getArrayWrapperLoadedIndex = createSelector(
  [getStateLoadedDataCache, getPropsScreenId, getCurrentContextParams],
  (loadedDataCache, screenId, contextParams) =>
    loadedDataCache[AutoLayoutService.getIndexScreenId(screenId, contextParams)]
);

export const getStateData = createSelector(
  [getStateDataCache, getPropsScreenId],
  (dataCache, screenId) => dataCache[screenId]
);

/**
 * Returns selectedScreenId controlled by ScreenSwitcher component.
 * It is used when screenId can be changed by user with DynamicAutoLayoutContainer component.
 */
export const getSelectedScreenId = createSelector(
  [getMiniScreensConfig, getSelectedScreenIds, getCurrentContextParams],
  (miniScreensConfig, selectedScreenIds, contextParams) =>
    miniScreensConfig &&
    miniScreensConfig.isLoaded() &&
    miniScreensConfig.getScreensByView(MINI_SCREENS_VIEWS_DOCUMENT).length > 0
      ? selectedScreenIds[
          AutoLayoutService.getContextToString(contextParams)
        ] || miniScreensConfig.getScreensByView(MINI_SCREENS_VIEWS_DOCUMENT)[0]
      : undefined
);

/**
 * Returns selectedScreenId controlled by ScreenSwitcher component or screenId
 * passed to AutoLayoutLoaderContainer (it is loaded into selectedScreenId in store).
 */
export const getCurrentScreenId = createSelector(
  [getSelectedScreenIds, getSelectedScreenId, getCurrentContextParams],
  (selectedScreenIds, selectedScreenId, contextParams) =>
    selectedScreenId ||
    selectedScreenIds[AutoLayoutService.getContextToString(contextParams)]
);

export const getStateMetadataByScreenId = createSelector(
  [getStateMetadataCache, getPropsScreenId],
  (metadataCache, screenId) =>
    new MetadataSchema(metadataCache[screenId]) || STATIC_EMPTY_METADATA_SCHEMA
);

export const getStateMetadata = createSelector(
  [getStateMetadataCache, getCurrentScreenId],
  (metadataCache, screenId) =>
    new MetadataSchema(metadataCache[screenId]) || STATIC_EMPTY_METADATA_SCHEMA
);

export const getArrayContent = createSelector(
  [getArrayContentSelector],
  (arrayContent) => arrayContent
);

export const getArrayLayout = createSelector(
  [getArrayLayoutSelector],
  (arrayLayout) => arrayLayout
);

export const getArrayWrapperDataChanges = createSelector(
  [getStateDataChanges, getArrayWrapperIndex, getPropsScreenId],
  (dataChanges, index, screenId) => {
    let result;
    if (screenId && index && index[KEYWORD_DATA_ITEMS] && dataChanges) {
      const indexScreenIds = map(
        index[KEYWORD_DATA_ITEMS],
        (item) => `${screenId}.${item}`
      );
      result = pick(dataChanges, [...indexScreenIds, index[KEYWORD_SCREEN_ID]]);
    }
    return result;
  }
);

export const isInputDataChanged = createSelector(
  [getStateDataChanges, getPropsScreenId, getPropsPathToData],
  (dataChanges, screenId, pathToData) =>
    screenId &&
    dataChanges &&
    pathToData &&
    dataChanges[screenId] &&
    !isNil(dataChanges[screenId][pathToData])
);

export const filterArrayItemsDataChanges = createSelector(
  [getStateDataChanges, getPropsScreenId],
  (dataChanges, screenId) => {
    const result = {};
    if (screenId && dataChanges && dataChanges[screenId]) {
      forEach(dataChanges[screenId], (item, key) => {
        const openBracketIndex = key.indexOf('[');
        if (openBracketIndex === -1) {
          result[key] = item;
        }
      });
    }
    return result;
  }
);

export const isUnsavedScreenAdded = createSelector(
  [getArrayWrapperIndex, getArrayWrapperLoadedIndex],
  (arrayWrapperIndex, arrayWrapperLoadedIndex) =>
    !isEmpty(arrayWrapperIndex) &&
    !isEmpty(arrayWrapperLoadedIndex) &&
    difference(
      arrayWrapperIndex[KEYWORD_DATA_ITEMS],
      arrayWrapperLoadedIndex[KEYWORD_DATA_ITEMS]
    ).length > 0
);

export const getDataSetIds = createSelector([getStateMetadata], (metadata) =>
  metadata.getDataSetIds()
);

export const areDataSetsLoaded = createSelector(
  [getStateMetadata, getDataSets, getDataSetIds],
  (metadata, dataSets, datasetIds) =>
    metadata && metadata.isLoaded() && every(datasetIds, (id) => dataSets[id])
);

export const getScreenPropertiesByScreenId = createSelector(
  [getScreenProperties, getPropsScreenId],
  (screenProperties, screenId) =>
    screenProperties && screenProperties[screenId]
      ? screenProperties[screenId]
      : screenPropertiesDefaultState
);

export const getScreenBannerConfig = createSelector(
  [getStateMetadata],
  (metadata) => {
    let screenBannerConfig;
    if (metadata.getLayout() && metadata.getLayout().getCustomConfig()) {
      screenBannerConfig = new ScreenBannerConfig(
        metadata.getLayout().getCustomConfig()[SCREEN_BANNER_CONFIG]
      );
    }
    return screenBannerConfig;
  }
);

export const getIndexScreenItemsData = createSelector(
  [getStateDataCache, getCurrentScreenId, getCurrentContextParams],
  (dataCache, screenId, contextParams) => {
    const data = {};
    const indexScreenId = AutoLayoutService.getParentArrayScreenId(
      screenId,
      contextParams
    );
    const arrayScreenData = dataCache[indexScreenId];
    const coreScreenId = AutoLayoutService.getCoreScreenId(indexScreenId);
    if (arrayScreenData && arrayScreenData.items) {
      forEach(arrayScreenData.items, (item) => {
        const itemScreenId = AutoLayoutService.getItemScreenId(
          coreScreenId,
          item
        );
        data[itemScreenId] =
          dataCache[itemScreenId] &&
          dataCache[itemScreenId][KEYWORD_DATA_CONTENT]
            ? dataCache[itemScreenId][KEYWORD_DATA_CONTENT]
            : undefined;
      });
    }
    return data;
  }
);

export const getIndexChecking = createSelector(
  [getStateIndexCheckings, getPropsScreenId, getCurrentContextParams],
  (indexCheckings, screenId, contextParams) =>
    indexCheckings[AutoLayoutService.getIndexScreenId(screenId, contextParams)]
);

export const getConflictLastChangeAuthor = createSelector(
  [
    getStateConflictLastChangeAuthor,
    getIndexChecking,
    getPropsScreenId,
    getCurrentContextParams,
  ],
  (conflictLastChangeAuthor, indexChecking, screenId, contextParams) =>
    conflictLastChangeAuthor[
      indexChecking &&
      indexChecking.invokeInfo &&
      indexChecking.invokeInfo.invokerScreenId
        ? indexChecking.invokeInfo.invokerScreenId
        : AutoLayoutService.getIndexScreenId(screenId, contextParams)
    ]
);

/** Validation * */
export const hasError = (state, autoLayoutStateId, screenId, key) =>
  state[autoLayoutStateId].validation[screenId] &&
  state[autoLayoutStateId].validation[screenId].errors[key];

export const getValidationMessage = (state, autoLayoutStateId, screenId, key) =>
  state[autoLayoutStateId].validation[screenId] &&
  state[autoLayoutStateId].validation[screenId].messages[key];

export const getValidationErrors = (state, autoLayoutStateId, screenId) =>
  state[autoLayoutStateId].validation[screenId] &&
  state[autoLayoutStateId].validation[screenId].errors;
